import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Container,
  Grid,
  Typography,
  Stack,
  Chip,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Tooltip
} from "@mui/material";
import theme from "../../styles/theme/theme";
import { useInView } from "react-intersection-observer";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { getPriceExchangeRate } from "../../features/user/userSlice";
import { formatPrice } from "../../utils/price";

// const countryData = {
//   India: [
//     { metal: "Aluminum Sheets", price: 210, change: 2, unit: "A$/Kg" },
//     { metal: "Copper Wire", price: 650, change: -10, unit: "A$/Kg" },
//     { metal: "Steel Scrap", price: 90, change: 0, unit: "A$/Kg" },
//     { metal: "Brass Scrap", price: 350, change: 5, unit: "A$/Kg" },
//     { metal: "Lead Scrap", price: 120, change: 3, unit: "A$/Kg" }, // Added
//   ],
//   China: [
//     { metal: "Copper Scrap", price: 47000, change: 150, unit: "¥/Ton" },
//     { metal: "Aluminum Extrusions", price: 19800, change: -100, unit: "¥/Ton" },
//     { metal: "Stainless Steel", price: 15800, change: 0, unit: "¥/Ton" },
//     { metal: "Zinc Scrap", price: 16500, change: 80, unit: "¥/Ton" }, // Added
//   ],
//   Dubai: [
//     { metal: "Copper Scrap", price: 14.5, change: 0.2, unit: "AED/Kg" },
//     { metal: "Aluminum Scrap", price: 5.8, change: 0, unit: "AED/Kg" },
//     { metal: "Mixed Scrap", price: 3.1, change: -0.1, unit: "AED/Kg" },
//     { metal: "Brass Scrap", price: 10.2, change: 0.3, unit: "AED/Kg" }, // Added
//   ],
//   Japan: [
//     { metal: "Copper Scrap", price: 890, change: 20, unit: "¥/Kg" },
//     { metal: "Aluminum Scrap", price: 280, change: -5, unit: "¥/Kg" },
//     { metal: "Iron Scrap", price: 55, change: 0, unit: "¥/Kg" },
//     { metal: "Stainless Steel", price: 320, change: 8, unit: "¥/Kg" }, // Added
//   ],
// };

const currencyMap = {
  UK: "GBP",
  China: "CNY",
  Dubai: "AED",
  Japan: "JPY",
  India: "INR",
};

const scrapCodeMap = {
  Global: "LME",
  India: "MCX",
  UK: "LME",
  China: "SHFE",
  Dubai: "DFM",
  Japan: "TOCOM",
};

const unitMap = [
  { label: "Kilogram (kg)", value: "kg" },
  { label: "Metric Ton (mt)", value: "mt" },
];

const currencySymbols = {
  GBP: "£",
  CNY: "¥",
  AED: "د.إ",
  JPY: "¥",
  INR: "A$",
  // USD: "$",
  // EUR: "€"
};

const HeroHomeSection = () => {
  // const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [country, setCountry] = useState("UK");
  const [unit, setUnit] = useState(unitMap[0].value);
  const { ref: heroRef } = useInView({ triggerOnce: true });
  const { priceExchangeRate, priceExchangeRateLoading } = useSelector(
    (state) => state.user
  );
  // const heroElements = useRef([]);
  // console.log("Price Exchange Rate:", priceExchangeRate.data);

  const { token, role } = useSelector((state) => state.auth);
  const isLoggedIn = Boolean(token);

  let redirectTo = "/buy";
  if (isLoggedIn && role === "buyer") {
    redirectTo = "/dashboard";
  } else if (isLoggedIn && role === "seller") {
    redirectTo = "/seller/dashboard";
  }

  useEffect(() => {
    // Fetch price exchange rate data here if needed
    console.log("Fetching price exchange rate for:", country, unit);
    dispatch(
      getPriceExchangeRate({
        currency: currencyMap[country],
        unit: unit,
      })
    );
  }, [dispatch, country, unit]);

  const getChangeValue = (change) => {
    if (typeof change === "string") {
      // Remove % and convert to number
      const num = parseFloat(change.replace("%", ""));
      if (isNaN(num)) return "-";
      return num > 0 ? `+${change}` : num < 0 ? change : "0";
    }
    if (typeof change === "number") {
      return change > 0 ? `+${change}` : change < 0 ? change : "0";
    }
    return "-";
  };

  return (
    <Box
      ref={heroRef}
      id="hero"
      sx={{
        minHeight: "80vh",
        display: "flex",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
        // background: `
        //   linear-gradient(135deg, rgba(244, 197, 66, 0.1) 0%, rgba(254, 253, 251, 1) 30%, rgba(244, 197, 66, 0.05) 100%)
        // `,
        background: "#fff",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          // background: `
          //   radial-gradient(circle at 20% 80%, rgba(244, 197, 66, 0.15) 0%, transparent 50%),
          //   radial-gradient(circle at 80% 20%, rgba(244, 197, 66, 0.1) 0%, transparent 50%)
          // `,
          pointerEvents: "none",
        },
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          position: "relative",
          zIndex: 2,
          py: { xs: 12, md: 8 },
        }}
      >
        <Grid container spacing={6} alignItems="center" justifyContent="center">
          {/* Left Content */}
          <Grid item xs={12} md={10} lg={6}>
            <Box sx={{ textAlign: { xs: "center", md: "center", lg: "left" } }}>
              <Chip
                label="Leading Scrap Marketplace"
                sx={{
                  mb: 4,
                  background:
                    "linear-gradient(135deg, rgba(244, 197, 66, 0.2) 0%, rgba(244, 197, 66, 0.1) 100%)",
                  color: "#B8860B",
                  border: "2px solid rgba(244, 197, 66, 0.3)",
                  fontWeight: 700,
                  fontSize: "12px",
                  px: 4,
                  py: 1.5,
                  borderRadius: "50px",
                  boxShadow: "0 8px 32px rgba(244, 197, 66, 0.2)",
                  mx: { xs: 0, md: "auto" },
                }}
              />

              <Typography
                component={motion.h1}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                sx={{
                  fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4.5rem" },
                  fontWeight: 700,
                  lineHeight: 1.1, // slightly increased for breathing
                  color: "#2D3A27",
                  letterSpacing: "-1.5px",
                  mb: 4, // 👈 adds space below heading
                }}
              >
                Transforming <br />
                <Box
                  component="span"
                  sx={{
                    background:
                      "linear-gradient(135deg, #F4C542 0%, #E6B73A 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Scrap into
                </Box>
                <br />
                <Box
                  component="span"
                  sx={{
                    background: "#2D3A27",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Opportunity
                </Box>
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  color: "rgba(45, 58, 39, 0.85)",
                  fontSize: { xs: "1.1rem", md: "1.4rem" },
                  fontWeight: 400,
                  lineHeight: 1.8,
                  maxWidth: "720px",
                  mx: { xs: "auto", lg: 0 },
                  px: { xs: 2, md: 0 },
                  mb: 6,
                  letterSpacing: "0.3px",
                }}
              >
                Buy & Sell Scrap Materials with{" "}
                <Box
                  component="span"
                  sx={{
                    background:
                      "linear-gradient(135deg, #F4C542 0%, #E6B73A 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontWeight: 600,
                    px: 0.3,
                  }}
                >
                  Transparency & Trust
                </Box>
                . Join the most trusted{" "}
                <Box
                  component="span"
                  sx={{ fontWeight: 500, color: "#2D3A27" }}
                >
                  global scrap trading community
                </Box>
                .
              </Typography>

              {/* CTA Buttons */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={3}
                sx={{
                  mb: 8,
                  justifyContent: { xs: "center", lg: "flex-start" },
                }}
              >
                {isLoggedIn ? (
                  <>
                    {/* Logged-in users (buyer or seller) → Dashboard */}
                    <Link to={redirectTo} style={{ textDecoration: "none" }}>
                      <Button
                        variant="contained"
                        size="medium"
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                          background:
                            "linear-gradient(135deg, #F4C542 0%, #E6B73A 100%)",
                          color: "#2D3A27",
                          borderRadius: "12px",
                          px: 5,
                          py: 2,
                          fontSize: "1rem",
                          fontWeight: 600,
                          textTransform: "none",
                          boxShadow: "0 10px 20px rgba(244, 197, 66, 0.3)",
                          minWidth: "160px",
                          position: "relative",
                          overflow: "hidden",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: "-100%",
                            width: "100%",
                            height: "100%",
                            background:
                              "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
                            transition: "left 0.5s",
                          },
                          "&:hover": {
                            transform: "translateY(-3px)",
                            boxShadow: "0 16px 32px rgba(244, 197, 66, 0.5)",
                            "&::before": { left: "100%" },
                          },
                        }}
                      >
                        Go to Dashboard
                      </Button>
                    </Link>

                    {/* Learn More (always available) */}
                    <Link to="/buy" style={{ textDecoration: "none" }}>
                      <Button
                        variant="outlined"
                        size="medium"
                        sx={{
                          borderColor: "#F4C542",
                          color: "#F4C542",
                          borderRadius: "12px",
                          px: 5,
                          py: 2,
                          fontSize: "1rem",
                          fontWeight: 600,
                          textTransform: "none",
                          borderWidth: "2px",
                          minWidth: "160px",
                          "&:hover": {
                            borderColor: "#E6B73A",
                            background: "rgba(244, 197, 66, 0.1)",
                            transform: "translateY(-3px)",
                          },
                        }}
                      >
                        Learn More
                      </Button>
                    </Link>
                  </>
                ) : (
                  <>
                    {/* Not logged in → Buy Scrap & Sell Scrap */}
                    <Link to="/buy" style={{ textDecoration: "none" }}>
                      <Button
                        variant="contained"
                        size="medium"
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                          background:
                            "linear-gradient(135deg, #F4C542 0%, #E6B73A 100%)",
                          color: "#2D3A27",
                          borderRadius: "12px",
                          px: 5,
                          py: 2,
                          fontSize: "1rem",
                          fontWeight: 600,
                          textTransform: "none",
                          boxShadow: "0 10px 20px rgba(244, 197, 66, 0.3)",
                          minWidth: "160px",
                          position: "relative",
                          overflow: "hidden",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: "-100%",
                            width: "100%",
                            height: "100%",
                            background:
                              "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
                            transition: "left 0.5s",
                          },
                          "&:hover": {
                            transform: "translateY(-3px)",
                            boxShadow: "0 16px 32px rgba(244, 197, 66, 0.5)",
                            "&::before": { left: "100%" },
                          },
                        }}
                      >
                        Buy Scrap
                      </Button>
                    </Link>

                    <Link to="/sell" style={{ textDecoration: "none" }}>
                      <Button
                        variant="outlined"
                        size="medium"
                        sx={{
                          borderColor: "#F4C542",
                          color: "#F4C542",
                          borderRadius: "12px",
                          px: 5,
                          py: 2,
                          fontSize: "1rem",
                          fontWeight: 600,
                          textTransform: "none",
                          borderWidth: "2px",
                          minWidth: "160px",
                          "&:hover": {
                            borderColor: "#E6B73A",
                            background: "rgba(244, 197, 66, 0.1)",
                            transform: "translateY(-3px)",
                          },
                        }}
                      >
                        Sell Scrap
                      </Button>
                    </Link>
                  </>
                )}
              </Stack>
            </Box>
          </Grid>

          {/* Right Table */}
          <Grid item xs={12} md={10} lg={6}>
            <Card
              sx={{
                borderRadius: "20px",
                boxShadow: "0 8px 24px rgba(244, 197, 66, 0.25)",
                background:
                  "linear-gradient(135deg, rgba(244,197,66,0.15), rgba(255,255,255,0.6))",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(244,197,66,0.3)",
                p: 3,
                width: { xs: "100%", sm: 800, md: 1000, lg: 600 }, // responsive widths
                maxWidth: "100%",
                mx: { xs: 0, md: "auto" },
              }}
            >


              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,                 // 🔥 equal spacing between items
                  mb: 2,
                  flexWrap: "wrap",       // responsive support
                }}
              >
                {/* Country + Scrap Code Combined */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    height: "40px",
                    backgroundColor: "rgba(255,255,255,0.8)",
                    borderRadius: "6px",
                    border: "1px solid #E0E0E0",
                    overflow: "hidden",
                  }}
                >
                  <Select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    variant="standard"
                    disableUnderline
                    size="small"
                    sx={{
                      px: 1.5,
                      minWidth: 90,
                      "& .MuiSelect-select": {
                        py: 0.5,
                        fontWeight: 600,
                        color: "#2D3A27",
                      },
                    }}
                  >
                    {Object.keys(currencyMap).map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>

                  <Box
                    sx={{
                      width: "1px",
                      height: "60%",
                      backgroundColor: "#E0E0E0",
                    }}
                  />

                  <Tooltip title="Scrap Trading Code" arrow placement="bottom">
                    <Box
                      sx={{
                        px: 1.5,
                        fontWeight: 600,
                        fontSize: "14px",
                        color: "#B8860B",
                      }}
                    >
                      {scrapCodeMap[country] || "-"}
                    </Box>
                  </Tooltip>
                </Box>

                {/* Unit Dropdown */}
                <Select
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  size="small"
                  sx={{
                    height: "40px",
                    backgroundColor: "rgba(255,255,255,0.8)",
                    borderRadius: "6px",
                    "& .MuiSelect-select": {
                      fontWeight: 600,
                      color: "#2D3A27",
                    },
                  }}
                >
                  {unitMap.map((u) => (
                    <MenuItem
                      key={u.value || "default"}
                      value={u.value}
                      disabled={u.value === ""}
                    >
                      {u.label}
                    </MenuItem>
                  ))}
                </Select>

                {/* View All Button */}
                <Button
                  variant="outlined"
                  sx={{
                    height: "40px",
                    borderRadius: "6px",
                    borderColor: "#F4C542",
                    color: "#B8860B",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "rgba(244,197,66,0.15)",
                      borderColor: "#E6B73A",
                    },
                  }}
                  onClick={() => navigate("/market")}
                >
                  View All
                </Button>
              </Box>

              {/* Table */}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: 700, color: "#2D3A27" }}
                        align="center"
                      >
                        Commodity(Metal)
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: "#2D3A27" }}
                        align="center"
                      >
                        Price ({priceExchangeRate.data?.overview[0]?.currency})
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: "#2D3A27" }}
                        align="center"
                      >
                        Change (%)
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, color: "#2D3A27" }}
                        align="center"
                      >
                        Unit ({priceExchangeRate.data?.overview[0]?.unit})
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {priceExchangeRateLoading ? (
                      Array.from({ length: 5 }).map((_, idx) => (
                        <TableRow key={idx}>
                          <TableCell align="center">
                            <Skeleton variant="text" width={120} />
                          </TableCell>
                          <TableCell align="center">
                            <Skeleton variant="text" width={80} />
                          </TableCell>
                          <TableCell align="center">
                            <Skeleton variant="text" width={60} />
                          </TableCell>
                          <TableCell align="center">
                            <Skeleton variant="text" width={80} />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : priceExchangeRate.data?.overview?.length ? (
                      priceExchangeRate.data?.overview
                        .slice(0, 5)
                        .map((row, idx) => (
                          <TableRow
                            key={idx}
                            sx={{
                              "&:nth-of-type(odd)": {
                                backgroundColor: "rgba(244,197,66,0.05)",
                              },
                            }}
                          >
                            <TableCell
                              sx={{ color: "#2D3A27", fontWeight: 500 }}
                              align="center"
                            >
                              {row.metal}
                            </TableCell>
                            <TableCell
                              sx={{ fontWeight: 600, color: "#15803d" }}
                              align="center"
                            >
                              {row.price ? row.price : "-"}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color:
                                  parseFloat(row.change_percent) > 0
                                    ? "#16a34a"
                                    : parseFloat(row.change_percent) < 0
                                      ? "#dc2626"
                                      : "#555",
                              }}
                              align="center"
                            >
                              {getChangeValue(row.change_percent)}
                            </TableCell>
                            {/* {console.log(
                              "Change:",
                              row.change_percent,
                              typeof row.change_percent
                            )} */}
                            <TableCell sx={{ color: "#2D3A27" }} align="center">
                              {row.currency}/{row.unit}
                            </TableCell>
                          </TableRow>
                        ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No Data
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography
                variant="body2"
                sx={{ mt: 2, color: "rgba(45,58,39,0.7)", fontStyle: "italic" }}
              >
                Global metal prices are displayed in local currency, with
                reference to LME and MCX data for India. Prices may vary based
                on market fluctuations.
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HeroHomeSection;
